/** @jsxImportSource @emotion/react */
import { FC, Fragment, useCallback, useMemo } from "react";
import { Button, Card, FormControl, Stack, Subheading } from "@contentful/f36-components";
import {
    FormalitySelect,
    FromLocaleSelect,
    GlossaryIndicator,
    PremiumCard,
    ToLocaleSelect,
} from ".";
import { ACTION_TYPE, useTranslationContext } from "../context";
import { useTranslation } from "../hooks";
import { FieldId, Formality, LabeledLocale, isFormalityEnabled, isGlossaryEnabled } from "../lib";
import { styles } from "../locations/styles";
import { mapLocale } from "../utils";

export const TranslationCard: FC = () => {
    const { state, dispatch } = useTranslationContext();
    const { translate } = useTranslation();

    const {
        translationRequests,
        isTranslating,
        formality,
        contentfulLocales,
        deeplSourceLocales,
        deeplTargetLocales,
    } = state;

    const setFormality = useCallback(
        (fieldId: FieldId, formality: Formality) => {
            dispatch({ type: ACTION_TYPE.SET_FORMALITY, payload: { fieldId, formality } });
        },
        [dispatch]
    );

    const translationFromLocales: LabeledLocale[] = useMemo(() => {
        return contentfulLocales.filter(
            locale => mapLocale(locale.locale, deeplSourceLocales) !== null
        );
    }, [contentfulLocales, deeplSourceLocales]);

    const translationToLocales: LabeledLocale[] = useMemo(() => {
        return contentfulLocales.filter(
            locale => mapLocale(locale.locale, deeplTargetLocales) !== null
        );
    }, [contentfulLocales, deeplTargetLocales]);

    const resetTranslationRequests = useCallback(() => {
        dispatch({
            type: ACTION_TYPE.SET_TRANSLATION_REQUESTS,
            payload: [],
        });
    }, [dispatch]);

    return (
        <Card>
            {translationRequests.map((translation, index) => (
                <Fragment key={`${translation.fieldId}-${translation.from}`}>
                    <Subheading fontSize="fontSizeXlHigh">{translation.label}</Subheading>
                    <FormControl as="fieldset">
                        <FormControl.Label as="legend">Translate From</FormControl.Label>
                        <FromLocaleSelect
                            translation={translation}
                            translationLocales={translationFromLocales}
                        />
                    </FormControl>
                    <FormControl as="fieldset">
                        <FormControl.Label as="legend">Translate To</FormControl.Label>
                        <ToLocaleSelect
                            translation={translation}
                            translationLocales={translationToLocales}
                        />
                    </FormControl>
                    <PremiumCard isInSidebar>
                        {isFormalityEnabled && (
                            <FormControl as="fieldset" marginBottom="none">
                                <FormalitySelect
                                    isInSidebar
                                    value={translation.formality || formality}
                                    onChange={e =>
                                        setFormality(
                                            translation.fieldId,
                                            e.target.value as Formality
                                        )
                                    }
                                />
                            </FormControl>
                        )}
                        {isGlossaryEnabled && <GlossaryIndicator />}
                    </PremiumCard>
                    {index !== translationRequests.length - 1 && <hr css={styles.splitter} />}
                </Fragment>
            ))}
            <Stack>
                <Button variant="primary" isLoading={isTranslating} onClick={translate}>
                    Translate
                </Button>
                <Button variant="transparent" onClick={resetTranslationRequests}>
                    Cancel
                </Button>
            </Stack>
        </Card>
    );
};
